import React, { useState, useEffect } from 'react';
import { useLarguraTela } from '../hooks/useLarguraTela';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: 18,
  },
  modal: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #dbe4ef',
    boxShadow: '0 18px 40px rgba(15,23,42,0.14)',
    width: 680,
    maxWidth: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: 56,
    padding: '0 18px',
    borderBottom: '1px solid #dbe4ef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#f8fafc',
    flexShrink: 0,
  },
  title: {
    color: '#0f172a',
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  btnClose: {
    padding: '7px 14px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 12,
    cursor: 'pointer',
  },
  body: {
    padding: '18px 20px 20px',
    overflowY: 'auto',
  },
  label: {
    color: '#334155',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    color: '#0f172a',
    fontSize: 14,
    marginBottom: 16,
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    color: '#0f172a',
    fontSize: 14,
    marginBottom: 16,
    outline: 'none',
  },
  row: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  btnCancel: {
    padding: '10px 20px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
  },
  btnSave: {
    padding: '10px 20px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  hint: {
    color: '#64748b',
    fontSize: 11,
    marginTop: -12,
    marginBottom: 16,
  },
};

const defaultBuildCommands = {
  react: 'npm install && npm run build',
  aspnet: 'dotnet publish -c Release -o ./publish',
  node: 'npm install',
};

function buildInitialForm(initialProject) {
  const project = initialProject || {};
  const type = project.type || 'react';

  return {
    id: project.id || '',
    name: project.name || '',
    type,
    repoPath: project.repoPath || '',
    branch: project.branch || 'main',
    buildCommand: project.buildCommand || defaultBuildCommands[type] || '',
    deployPath: project.deployPath || '',
    buildOutputDir: project.buildOutputDir || '',
    description: project.description || '',
    enabled: typeof project.enabled === 'boolean' ?project.enabled : true,
    iisSiteName: project.iisSiteName || '',
    poolName: project.poolName || project.iisAppPoolName || '',
    publishPath: project.publishPath || './publish',
    processName: project.processName || '',
    startCommand: project.startCommand || '',
  };
}

export default function AddProjectModal({ api = null, onClose, onSave, initialProject = null }) {
  const isEditing = Boolean(initialProject);
  const larguraTela = useLarguraTela();
  const ehMobile = larguraTela <= 900;
  const [form, setForm] = useState(() => buildInitialForm(initialProject));

  // Recursos do IIS (AppPools e Sites) lidos do Windows para preenchimento assistido
  const [iisPools, setIisPools] = useState([]);
  const [iisSites, setIisSites] = useState([]);
  const [carregandoIis, setCarregandoIis] = useState(false);
  const [erroIis, setErroIis] = useState('');
  const [iisCarregado, setIisCarregado] = useState(false);

  useEffect(() => {
    let ativo = true;

    if (form.type !== 'aspnet' || !api?.getIisResources || iisCarregado || carregandoIis) {
      return undefined;
    }

    setCarregandoIis(true);
    setErroIis('');

    api
      .getIisResources()
      .then((dados) => {
        if (!ativo) return;
        setIisPools(Array.isArray(dados?.pools) ? dados.pools : []);
        setIisSites(Array.isArray(dados?.sites) ? dados.sites : []);
        setIisCarregado(true);
      })
      .catch((err) => {
        if (!ativo) return;
        setErroIis(err?.message || 'Não foi possível ler os recursos do IIS');
      })
      .finally(() => {
        if (ativo) setCarregandoIis(false);
      });

    return () => {
      ativo = false;
    };
  }, [form.type, api, iisCarregado, carregandoIis]);

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    if (field === 'type') {
      newForm.buildCommand = defaultBuildCommands[value] || '';
    }
    setForm(newForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const project = { ...form };

    if (project.type !== 'aspnet') {
      delete project.iisSiteName;
      delete project.poolName;
      delete project.publishPath;
    }
    if (project.type !== 'node') {
      delete project.processName;
      delete project.startCommand;
    }

    delete project.deployStatus;
    delete project.lastDeploy;
    delete project.history;
    delete project.logs;

    Object.keys(project).forEach((k) => {
      if (project[k] === '') delete project[k];
    });

    if (!project.id) delete project.id;

    onSave(project);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <form
        style={{
          ...styles.modal,
          width: ehMobile ?'100%' : styles.modal.width,
          maxHeight: ehMobile ?'100dvh' : styles.modal.maxHeight,
          borderRadius: ehMobile ?0 : styles.modal.borderRadius,
        }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div style={{ ...styles.header, padding: ehMobile ?'0 12px' : styles.header.padding }}>
          <div style={styles.title}>{isEditing ?'Editar projeto' : 'Adicionar projeto'}</div>
          <button type="button" style={styles.btnClose} onClick={onClose}>
            Fechar
          </button>
        </div>

        <div style={{ ...styles.body, padding: ehMobile ?'14px 12px 18px' : styles.body.padding }}>
          <label style={styles.label}>ID do projeto</label>
          <input
            style={styles.input}
            value={form.id}
            onChange={(e) => update('id', e.target.value)}
            placeholder="headthink-one-api"
          />
          <div style={styles.hint}>
            Opcional ao criar. Se vazio, será gerado automaticamente pelo nome.
          </div>

          <label style={styles.label}>Nome do projeto *</label>
          <input
            style={styles.input}
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Meu app"
            required
          />

          <label style={styles.label}>Tipo *</label>
          <select style={styles.select} value={form.type} onChange={(e) => update('type', e.target.value)}>
            <option value="react">React</option>
            <option value="aspnet">ASP.NET Core</option>
            <option value="node">Node.js</option>
          </select>

          <label style={styles.label}>Projeto ativo</label>
          <select
            style={styles.select}
            value={form.enabled ?'true' : 'false'}
            onChange={(e) => update('enabled', e.target.value === 'true')}
          >
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>

          <label style={styles.label}>Caminho do repositório *</label>
          <input
            style={styles.input}
            value={form.repoPath}
            onChange={(e) => update('repoPath', e.target.value)}
            placeholder="C:\\Projects\\meu-app"
            required
          />
          <div style={styles.hint}>Caminho completo da pasta do projeto no servidor.</div>

          <label style={styles.label}>Branch</label>
          <input
            style={styles.input}
            value={form.branch}
            onChange={(e) => update('branch', e.target.value)}
            placeholder="main"
          />

          <label style={styles.label}>Comando de build</label>
          <input
            style={styles.input}
            value={form.buildCommand}
            onChange={(e) => update('buildCommand', e.target.value)}
          />

          <label style={styles.label}>Descrição</label>
          <input
            style={styles.input}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Descrição opcional"
          />

          <label style={styles.label}>Pasta de saída do build</label>
          <input
            style={styles.input}
            value={form.buildOutputDir}
            onChange={(e) => update('buildOutputDir', e.target.value)}
            placeholder="dist"
          />
          <div style={styles.hint}>
            Pasta onde o build gera os arquivos (ex: "dist" para Vite, "build" para CRA). Deixe vazio para detectar automaticamente.
          </div>

          <label style={styles.label}>Pasta destino (Deploy Path)</label>
          <input
            style={styles.input}
            value={form.deployPath}
            onChange={(e) => update('deployPath', e.target.value)}
            placeholder="C:\\inetpub\\wwwroot\\meu-app"
          />
          <div style={styles.hint}>
            Pasta onde os arquivos buildados serão copiados. Deixe vazio para manter no repositório.
          </div>

          {form.type === 'aspnet' && (
            <>
              <label style={styles.label}>Nome do site no IIS *</label>
              <input
                style={styles.input}
                list="iis-sites-list"
                value={form.iisSiteName}
                onChange={(e) => update('iisSiteName', e.target.value)}
                placeholder="MeuSiteIIS"
                required
              />
              <datalist id="iis-sites-list">
                {iisSites.map((site) => (
                  <option key={site} value={site} />
                ))}
              </datalist>
              <div style={styles.hint}>
                {carregandoIis
                  ? 'Lendo sites do IIS...'
                  : erroIis
                  ? `Nome exato do site no IIS. (Lista automática indisponível: ${erroIis})`
                  : 'Selecione da lista lida do IIS ou digite o nome exato.'}
              </div>

              <label style={styles.label}>Nome do App Pool no IIS</label>
              <input
                style={styles.input}
                list="iis-pools-list"
                value={form.poolName}
                onChange={(e) => update('poolName', e.target.value)}
                placeholder="MeuAppPoolIIS"
              />
              <datalist id="iis-pools-list">
                {iisPools.map((pool) => (
                  <option key={pool} value={pool} />
                ))}
              </datalist>
              <div style={styles.hint}>
                {carregandoIis
                  ? 'Lendo App Pools do IIS...'
                  : erroIis
                  ? `Opcional (poolName). Se vazio, o sistema localiza pelo site. (Lista automática indisponível: ${erroIis})`
                  : 'Selecione da lista lida do IIS para garantir o nome exato. Se vazio, o sistema localiza pelo site.'}
              </div>

              <label style={styles.label}>Caminho de publish</label>
              <input
                style={styles.input}
                value={form.publishPath}
                onChange={(e) => update('publishPath', e.target.value)}
                placeholder="./publish"
              />
            </>
          )}

          {form.type === 'node' && (
            <>
              <label style={styles.label}>Nome do processo (pm2 ou título da janela)</label>
              <input
                style={styles.input}
                value={form.processName}
                onChange={(e) => update('processName', e.target.value)}
                placeholder="meu-backend"
              />

              <label style={styles.label}>Comando de start</label>
              <input
                style={styles.input}
                value={form.startCommand}
                onChange={(e) => update('startCommand', e.target.value)}
                placeholder="node index.js"
              />
            </>
          )}

          <div style={{ ...styles.row, flexDirection: ehMobile ?'column-reverse' : 'row' }}>
            <button type="button" style={{ ...styles.btnCancel, width: ehMobile ?'100%' : 'auto' }} onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" style={{ ...styles.btnSave, width: ehMobile ?'100%' : 'auto' }}>
              {isEditing ?'Salvar alterações' : 'Salvar projeto'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

